import { Box, Text, Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import {
  getSelectedDashboardScenario,
  getDashboardReportingPeriod,
  getCompareDashboardScenario,
} from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import { getReportingPeriodDisplay } from 'utils/reportingPeriodUtils';

import { PageInstrumentData, PageInstruments } from './Instruments/proptypes';
import PageGrid from './PageGrid';

export interface PreviewProperties {
  selectedPages: any[];
  allPageInstruments: PageInstruments;
  allInstrumentsData: PageInstrumentData;
  isPageDataLoading: boolean;
  isPageInstrumentsLoading: boolean;
  onPageHeightChange?: (pageId: string, height: number) => void;
}

const Preview = ({
  selectedPages,
  allPageInstruments,
  allInstrumentsData,
  isPageDataLoading,
  isPageInstrumentsLoading,
  onPageHeightChange,
}: PreviewProperties) => {
  const selectedScenario = useSelector(getSelectedDashboardScenario);
  const dashboardReportingPeriod = useSelector(getDashboardReportingPeriod);
  const compareScenario = useSelector(getCompareDashboardScenario);
  const model = useSelector(getBlocks);
  const fyStartMonth = useMemo(() => {
    const month = model?.time_properties?.fy_start_month;
    return (month as string | undefined) ?? undefined;
  }, [model?.time_properties?.fy_start_month]);

  if (isPageDataLoading || isPageInstrumentsLoading) {
    return <Box>Loading preview...</Box>;
  }

  return (
    <Box width='1440px' display='flex' flexDirection='column' alignItems='center' gap={6}>
      {selectedPages
        .sort((a: any, b: any) => a.position - b.position)
        .map((page: any, index: number) => {
          const reportingPeriodDisplay = dashboardReportingPeriod
            ? getReportingPeriodDisplay(
                dashboardReportingPeriod,
                fyStartMonth,
                dashboardReportingPeriod.preset || 'Custom',
              )
            : null;
          const hasDescription = Boolean(page?.description);

          return (
            <Box
              key={page.id}
              className={`page-container page-${page.id} ${index === 0 ? '' : 'page-break'}`}
              data-page-number={`Page ${index + 1} of ${selectedPages.length}`}
            >
              <Text fontWeight='bold' mb={hasDescription ? 0 : 2} color='black' fontSize='xl'>
                {page.name}
              </Text>
              {hasDescription && (
                <Text color='black' fontSize='md' mb={1}>
                  {page.description}
                </Text>
              )}
              <Flex flexWrap='wrap' gap={1} mb={1}>
                <Box>
                  <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                    {` Scenario : ${selectedScenario?.name || 'No scenario selected'} `}
                  </Text>
                </Box>
                {compareScenario ? (
                  <Box>
                    <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                      {`vs ${compareScenario?.name || ''} `}
                    </Text>
                  </Box>
                ) : null}
                {reportingPeriodDisplay ? (
                  <Box>
                    <Text fontSize='12px' color='black' cursor='pointer' noOfLines={1}>
                      {`| Reporting Period : ${reportingPeriodDisplay}`}
                    </Text>
                  </Box>
                ) : null}
              </Flex>
              <PageGrid
                page={page}
                allInstruments={allPageInstruments[page.id] || []}
                allInstrumentsData={allInstrumentsData[page.id] || {}}
                onHeightChange={onPageHeightChange}
              />
            </Box>
          );
        })}
    </Box>
  );
};

export default Preview;
